import { onlyNumber } from '../Formatter';

export const currency = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  let numberToFormat: number;

  if (typeof value === 'number') {
    numberToFormat = value;
  } else {
    numberToFormat = Number(onlyNumber(value)) / 100;
  }

  if (Number.isNaN(numberToFormat)) {
    return '';
  }

  return new Intl.NumberFormat('pt-br', {
    style: 'currency',
    currency: 'BRL',
  }).format(numberToFormat);
};

export const currencyNoSymbol = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  let numberToFormat: number;

  if (typeof value === 'number') {
    numberToFormat = value;
  } else {
    numberToFormat = Number(onlyNumber(value)) / 100;
  }

  if (Number.isNaN(numberToFormat) || numberToFormat === 0) {
    return '';
  }

  return new Intl.NumberFormat('pt-br', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberToFormat);
};

export const currencyToNumber = (value: string): number => {
  if (!value) return 0;

  // Remove currency symbols and spaces first (R$, BRL, $, etc.)
  const withoutSymbols = value
    .replace(/[R\$\s]/g, '')
    .trim();

  if (!withoutSymbols) return 0;

  // Brazilian format: 1.234,56 (period = thousands, comma = decimal)
  // Strategy: if comma exists and a period follows it, periods are thousands separators
  // Otherwise, determine by checking if there are multiple commas or the last comma separates 2 digits
  const hasComma = withoutSymbols.includes(',');
  const hasPeriod = withoutSymbols.includes('.');

  let normalized: string;

  if (hasComma && hasPeriod) {
    // Both present: remove all periods (thousands separators), replace comma with period
    normalized = withoutSymbols.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    // Only comma: treat as decimal separator (Brazilian format)
    normalized = withoutSymbols.replace(',', '.');
  } else {
    // No separators: treat as integer
    normalized = withoutSymbols;
  }

  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const cpf = (value: string) =>
  onlyNumber(`${value}`)
    .replace(/(\d{11})(\d)/, '$1')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

export const cnpj = (value: string) =>
  onlyNumber(`${value}`)
    .replace(/(\d{14})(\d)/, '$1')
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');

export const cpfOrCnpj = (value: string) => {
  if (`${value}`.length < 15) {
    return onlyNumber(`${value}`)
      .replace(/(\d{11})(\d)/, '$1')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return onlyNumber(`${value}`)
    .replace(/(\d{14})(\d)/, '$1')
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

export const cep = (value: string | number) => {
  const number = onlyNumber(`${value}`);
  if (number.length <= 3) {
    return number;
  }

  return number.substr(0, 8).replace(/([0-9]{5})([0-9]{1,3})/, '$1-$2');
};

export const agency = (value: string) => {
  if (value.length > 0) {
    return onlyNumber(value).toString().slice(0, 4);
  }
  return value;
};

export const account = (value: string) => {
  const valueAux = value.split('-').join('');
  if (valueAux.length < 2) {
    return valueAux;
  }
  return `${valueAux.substr(0, valueAux.length - 1)}-${valueAux.substr(valueAux.length - 1)}`;
};

export const internationalPhone = (value: string | number) =>
  onlyNumber(value)
    .replace(/^(\d{1,2})/, '+$1')
    .replace(/(\d{2})(\d{1})/, '$1 $2');

export const brPhone = (value: string) =>
  onlyNumber(value)
    .replace(/^(\d{1,2})/, '($1')
    .replace(/(\d{2})(\d{1})/, '$1) $2')
    .replace(/(\+\d{2} \(\d{2})(\d)/, '$1) $2')
    .replace(/(\d{4})(\d{1,4})/, '$1-$2')
    .replace(/(\d{5})(\d{5})/, '$1-$2')
    .replace(/(-\d{5})\d+?$/, '$1')
    .replace(/(\d{4})-(\d{1})(\d{4})/, '$1$2-$3');

export const phone = (value: string | number) =>
  onlyNumber(`${value}`)
    .replace(/^(\d{1,2})/, '+$1')
    .replace(/(\d{2})(\d{1})/, '$1 ($2')
    .replace(/(\+\d{2} \(\d{2})(\d)/, '$1) $2')
    .replace(/(\d{4})(\d{1,4})/, '$1-$2')
    .replace(/(\d{5})(\d{5})/, '$1-$2')
    .replace(/(-\d{5})\d+?$/, '$1')
    .replace(/(\d{4})-(\d{1})(\d{4})/, '$1$2-$3');

export const phoneDDD = (value: string | number) =>
  onlyNumber(`${value}`)
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2');

export const phoneToNumber = (value: string): string => `+${Number(onlyNumber(value))}`;

export const hour = (value: string | number) =>
  onlyNumber(value)
    .replace(/(\d{4})(\d)/, '$1')
    .replace(/(\d{2})(\d)/, '$1:$2');

export const integerNumber = (value: string | number) => onlyNumber(value);

export const date = (value: string) => {
  let v = value.replace(/\D/g, '');

  if (v.length > 2) v = `${v.slice(0, 2)}/${v.slice(2)}`;
  if (v.length > 5) v = `${v.slice(0, 5)}/${v.slice(5)}`;
  if (v.length > 10) v = v.slice(0, 10);

  return v;
};
